// Dashboard › Planner › Pick Advisor (Alex, 2026-10-07).
//
// What the Pick Advisor is telling a free picker on the default Low
// queue RIGHT NOW, per warehouse, and why — so a team leader can see
// the same recommendation the handhelds show, the live stage state it
// was decided from (waves waiting at the bays / wall, sorts in
// progress, waves in flight, orders ready to pack, who is active), the
// derived sort load and pack runway, and how today's wave starts went
// against the advice. Read-only; polled every 15 s while visible.
// Pickers never see the reason — this is the leader's view of it.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, CheckCircle2, Compass, Package, RefreshCw, Shuffle, Users } from "lucide-react";
import { useTranslation } from "../i18n";
import { fetchPickAdvisor, PickAdvisorPayload, PickAdvisorRecommendation, PickAdvisorWarehouse } from "../api/dashboardActivity";
import { formatClock, formatDuration } from "./helpers/plannerMath";

const POLL_MS = 15_000;

const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmtTime = (iso: string | null | undefined) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "—";
    }
};
const minutesSince = (iso: string | null | undefined, nowMs: number) =>
    iso ? Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 60_000)) : null;

/** Reason fields that are minutes from midnight → shown as a clock. */
const MINUTE_REASON_KEYS = new Set(["lands_at", "packed_by", "latest_start", "last_start"]);
/** Reason fields shown, in this order; anything else is skipped. */
const REASON_KEYS = [
    "orders", "lands_at", "packed_by", "latest_start", "last_start", "sort_load", "pack_runway",
    "large_due", "due", "include", "ps", "put_wall", "large", "light",
];

/** The stable key the BE logs for a recommendation / a start. */
const pickKeyOf = (p: PickAdvisorRecommendation["pick"]) => {
    if (!p) return "none";
    if (p.pickType === "pick_and_sort") return p.sortStation === "put_wall" ? "put_wall" : (p.includePutWall ? "pick_and_sort+put_wall" : "pick_and_sort");
    return p.pickType;
};

interface Props {
    warehouseFilter: string;
    /** Wall clock source — tests pin it. */
    clock?: () => number;
}

const DashboardPickAdvisor = ({ warehouseFilter, clock = Date.now }: Props) => {
    const { t } = useTranslation();
    const [payload, setPayload] = useState<PickAdvisorPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [nowMs, setNowMs] = useState(() => clock());
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            if (!alive || document.visibilityState === "hidden") return;
            setLoading(true);
            try {
                const p = await fetchPickAdvisor(warehouseFilter);
                if (!alive) return;
                setPayload(p);
                setFailed(false);
                setNowMs(clock());
            } catch {
                if (!alive) return;
                setFailed(true);
                toast.error(t("server_error"), { toastId: "dashboard-admin-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        };
        tick();
        const id = window.setInterval(tick, POLL_MS);
        const onVisible = () => { if (document.visibilityState === "visible") tick(); };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            alive = false;
            window.clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [warehouseFilter, reloadTick, clock, t]);

    // Labels. A rule's label and one-line explanation live in i18n under
    // its key; an unknown rule (a newer BE) shows its raw key.
    const ruleLabel = (rule: string | null | undefined) => {
        if (!rule) return "—";
        const key = `dashboard_advisor_rule_${rule}`;
        const v = t(key);
        return v === key ? rule : v;
    };
    const ruleWhy = (rule: string | null | undefined) => {
        if (!rule) return null;
        const key = `dashboard_advisor_rule_${rule}_why`;
        const v = t(key);
        return v === key ? null : v;
    };
    const typeLabel = (type: string | null | undefined) => {
        switch (type) {
            case "light": return t("picking_pick_type_light");
            case "pick_and_sort": return t("picking_pick_type_pick_and_sort");
            case "put_wall": return t("picking_pick_type_put_wall");
            case "large": return t("picking_pick_type_large");
            case "pick_and_sort+put_wall": return `${t("picking_pick_type_pick_and_sort")} + ${t("picking_pick_type_put_wall")}`;
            case "none": return t("dashboard_advisor_pick_none");
            default: return type ?? "—";
        }
    };
    const pickLabel = (rec: PickAdvisorRecommendation) => {
        if (!rec.pick) return t("dashboard_advisor_pick_none");
        const key = pickKeyOf(rec.pick);
        return rec.pick.orders > 0
            ? t("dashboard_advisor_pick_with_orders", { pick: typeLabel(key), n: fmtInt(rec.pick.orders) })
            : typeLabel(key);
    };
    const reasonLabel = (key: string) => {
        const k = `dashboard_advisor_reason_${key}`;
        const v = t(k);
        return v === k ? key : v;
    };
    const reasonValue = (key: string, v: unknown) => {
        if (v === null || v === undefined) return "—";
        if (typeof v === "boolean") return v ? t("yes") : t("no");
        if (typeof v === "number") {
            if (MINUTE_REASON_KEYS.has(key)) return formatClock(v);
            if (key === "sort_load" || key === "pack_runway") return formatDuration(v);
            return fmtInt(v);
        }
        return String(v);
    };
    const minutesLabel = (n: number | null | undefined) =>
        n === null || n === undefined ? "—" : formatDuration(n);

    const followedBadge = (followed: boolean | null) =>
        followed === null
            ? <span className="dash-badge dash-planner-badge--muted">{t("dashboard_advisor_followed_na")}</span>
            : followed
                ? <span className="dash-badge dash-planner-badge--ok">{t("dashboard_advisor_followed_yes")}</span>
                : <span className="dash-badge dash-planner-badge--late">{t("dashboard_advisor_followed_no")}</span>;

    const recommendation = (w: PickAdvisorWarehouse, enabled: boolean) => {
        const rec = w.advisor;
        if (!enabled) {
            return (
                <div className="dash-advisor-rec dash-advisor-rec--off" data-testid={`advisor-rec-${w.warehouse}`}>
                    <span className="dash-advisor-pick">{t("dashboard_advisor_off")}</span>
                    <span className="dash-advisor-why">{t("dashboard_advisor_off_hint")}</span>
                </div>
            );
        }
        if (!rec) {
            return (
                <div className="dash-advisor-rec dash-advisor-rec--off" data-testid={`advisor-rec-${w.warehouse}`}>
                    <span className="dash-advisor-pick">{w.advisor_error ? t("dashboard_advisor_error") : t("dashboard_advisor_none")}</span>
                </div>
            );
        }
        const pr = rec.projections ?? {};
        const why = ruleWhy(rec.rule);
        const askLeader = rec.pick === null;
        const reasonEntries = REASON_KEYS
            .filter((k) => rec.reason && k in rec.reason)
            .map((k) => [k, (rec.reason as Record<string, unknown>)[k]] as const);
        const pools = rec.pools ?? {};
        return (
            <div className={`dash-advisor-rec${askLeader ? " dash-advisor-rec--ask" : ""}`} data-testid={`advisor-rec-${w.warehouse}`}>
                <div className="dash-advisor-rec-head">
                    <span className="dash-advisor-rec-label">
                        {askLeader ? <AlertTriangle size={16} /> : <Compass size={16} />}
                        {t("dashboard_advisor_recommending")}
                    </span>
                    <span className="dash-advisor-pick" data-testid={`advisor-pick-${w.warehouse}`}>{pickLabel(rec)}</span>
                    <span className="dash-advisor-rule" data-testid={`advisor-rule-${w.warehouse}`}>{ruleLabel(rec.rule)}</span>
                    {why && <span className="dash-advisor-why">{why}</span>}
                </div>
                <div className="dash-advisor-grid">
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_sort_load")}</span>
                        <span className="dash-advisor-stat-value">{minutesLabel(typeof pr.sort_load_min === "number" ? pr.sort_load_min : w.load.sort_load_min)}</span>
                        <span className="dash-advisor-stat-sub">{t("dashboard_advisor_sort_load_sub")}</span>
                    </div>
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_pack_runway")}</span>
                        <span className="dash-advisor-stat-value">
                            {typeof pr.pack_runway_min === "number" ? formatDuration(pr.pack_runway_min)
                                : w.load.pack_runway_min !== null ? formatDuration(w.load.pack_runway_min)
                                    : t("dashboard_advisor_no_packers")}
                        </span>
                        <span className="dash-advisor-stat-sub">{t("dashboard_advisor_pack_runway_sub")}</span>
                    </div>
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_sorters")}</span>
                        <span className="dash-advisor-stat-value">
                            {t("dashboard_advisor_now_expected", {
                                now: String(pr.sorters_now ?? w.state.sort.sorters_now),
                                expected: String(pr.sorters_expected ?? w.state.sort.sorters_expected),
                            })}
                        </span>
                        <span className="dash-advisor-stat-sub">{t("dashboard_advisor_sorters_sub", { n: String(w.state.sort.typical_sorters) })}</span>
                    </div>
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_packers")}</span>
                        <span className="dash-advisor-stat-value">{String(pr.packers_now ?? w.state.pack.packers_now)}</span>
                        <span className="dash-advisor-stat-sub">{t("dashboard_advisor_packers_sub")}</span>
                    </div>
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_lands_at")}</span>
                        <span className="dash-advisor-stat-value">{typeof pr.lands_at === "string" ? pr.lands_at : "—"}</span>
                        <span className="dash-advisor-stat-sub">{t("dashboard_advisor_lands_at_sub")}</span>
                    </div>
                    <div className="dash-advisor-stat">
                        <span className="dash-advisor-stat-label">{t("dashboard_advisor_packed_by")}</span>
                        <span className="dash-advisor-stat-value">{typeof pr.packed_by === "string" ? pr.packed_by : "—"}</span>
                        <span className="dash-advisor-stat-sub">
                            {rec.clocks?.ship_cutoff ? t("dashboard_advisor_packed_by_sub", { cutoff: rec.clocks.ship_cutoff }) : ""}
                        </span>
                    </div>
                </div>
                {reasonEntries.length > 0 && (
                    <div className="dash-advisor-chips" data-testid={`advisor-reason-${w.warehouse}`}>
                        {reasonEntries.map(([k, v]) => (
                            <span key={k} className="dash-advisor-chip">
                                <span className="dash-advisor-chip-label">{reasonLabel(k)}</span>
                                <span className="dash-advisor-chip-value">{reasonValue(k, v)}</span>
                            </span>
                        ))}
                    </div>
                )}
                <p className="dash-advisor-pools">
                    {t("dashboard_advisor_pools", {
                        ps: fmtInt(Number(pools.ps ?? 0)),
                        wall: pools.putWallEnabled === true ? fmtInt(Number(pools.putWall ?? 0)) : t("dashboard_advisor_wall_off"),
                        large: fmtInt(Number(pools.large ?? 0)),
                        light: fmtInt(Number(pools.light ?? 0)),
                    })}
                    {pr.after_cutoff === true && ` · ${t("dashboard_advisor_after_cutoff")}`}
                </p>
            </div>
        );
    };

    const stageTiles = (w: PickAdvisorWarehouse) => {
        const s = w.state;
        const byType = new Map<string, number>();
        for (const wave of s.inflight) byType.set(wave.type, (byType.get(wave.type) ?? 0) + 1);
        const inflightLabel = [...byType.entries()].map(([type, n]) => `${n} ${typeLabel(type)}`).join(" · ");
        const readyLabel = Object.entries(s.pack.ready_by_type)
            .filter(([, n]) => n > 0)
            .map(([type, n]) => `${fmtInt(n)} ${typeLabel(type)}`)
            .join(" · ");
        return (
            <div className="dash-planner-tiles dash-advisor-tiles">
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Shuffle size={14} />{t("dashboard_advisor_tile_sort")}</span>
                    <span className="dash-planner-tile-value" data-testid={`advisor-sort-${w.warehouse}`}>
                        {t("dashboard_advisor_tile_sort_value", { waiting: String(s.sort.waiting.length), busy: String(s.sort.busy.length) })}
                    </span>
                    <span className="dash-planner-tile-sub">
                        {t("dashboard_advisor_tile_sort_sub", {
                            items: fmtInt(s.sort.waiting.reduce((a, x) => a + x.items, 0) + s.sort.busy.reduce((a, x) => a + (x.remaining ?? 0), 0)),
                            slots: String(s.sort.wall_slots_free),
                        })}
                    </span>
                </div>
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Compass size={14} />{t("dashboard_advisor_tile_inflight")}</span>
                    <span className="dash-planner-tile-value" data-testid={`advisor-inflight-${w.warehouse}`}>{s.inflight.length}</span>
                    <span className="dash-planner-tile-sub">{inflightLabel || t("dashboard_advisor_tile_none")}</span>
                </div>
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Package size={14} />{t("dashboard_advisor_tile_pack")}</span>
                    <span className="dash-planner-tile-value" data-testid={`advisor-pack-${w.warehouse}`}>{fmtInt(s.pack.ready_orders)}</span>
                    <span className="dash-planner-tile-sub">{readyLabel || t("dashboard_advisor_tile_none")}</span>
                </div>
                <div className="dash-planner-tile">
                    <span className="dash-planner-tile-label"><Users size={14} />{t("dashboard_advisor_tile_people")}</span>
                    <span className="dash-planner-tile-value" data-testid={`advisor-people-${w.warehouse}`}>
                        {t("dashboard_advisor_tile_people_value", { pickers: String(s.pickers_now), sorters: String(s.sort.sorters_now), packers: String(s.pack.packers_now) })}
                    </span>
                    <span className="dash-planner-tile-sub">{t("dashboard_advisor_tile_people_sub")}</span>
                </div>
            </div>
        );
    };

    const waveTables = (w: PickAdvisorWarehouse) => {
        const s = w.state;
        return (
            <div className="dash-advisor-tables">
                <section className="dash-advisor-table">
                    <h4 className="dash-advisor-table-title">{t("dashboard_advisor_waiting_title")}</h4>
                    {s.sort.waiting.length === 0 && s.sort.busy.length === 0 ? (
                        <p className="dash-advisor-empty">{t("dashboard_advisor_waiting_none")}</p>
                    ) : (
                        <table className="dash-table dash-advisor-dash-table">
                            <thead>
                                <tr>
                                    <th>{t("dashboard_advisor_col_wave")}</th>
                                    <th>{t("dashboard_advisor_col_station")}</th>
                                    <th>{t("dashboard_advisor_col_orders")}</th>
                                    <th>{t("dashboard_advisor_col_items")}</th>
                                    <th>{t("dashboard_advisor_col_status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {s.sort.busy.map((x) => (
                                    <tr key={`busy:${x.wave_id}`} data-testid={`advisor-sort-row-${x.wave_id}`}>
                                        <td className="dash-td-primary" data-label={t("dashboard_advisor_col_wave")}>#{x.wave_id}</td>
                                        <td data-label={t("dashboard_advisor_col_station")}>{typeLabel(x.station)}</td>
                                        <td data-label={t("dashboard_advisor_col_orders")}>{fmtInt(x.orders)}</td>
                                        <td data-label={t("dashboard_advisor_col_items")}>{fmtInt(x.remaining ?? 0)} / {fmtInt(x.items)}</td>
                                        <td data-label={t("dashboard_advisor_col_status")}><span className="dash-badge dash-planner-badge--ok">{t("dashboard_advisor_status_sorting")}</span></td>
                                    </tr>
                                ))}
                                {s.sort.waiting.map((x) => {
                                    const mins = minutesSince(x.completed_at, nowMs);
                                    return (
                                        <tr key={`wait:${x.wave_id}`} data-testid={`advisor-sort-row-${x.wave_id}`}>
                                            <td className="dash-td-primary" data-label={t("dashboard_advisor_col_wave")}>#{x.wave_id}</td>
                                            <td data-label={t("dashboard_advisor_col_station")}>{typeLabel(x.station)}</td>
                                            <td data-label={t("dashboard_advisor_col_orders")}>{fmtInt(x.orders)}</td>
                                            <td data-label={t("dashboard_advisor_col_items")}>{fmtInt(x.items)}</td>
                                            <td data-label={t("dashboard_advisor_col_status")}>
                                                <span className="dash-badge dash-planner-badge--warn">
                                                    {mins === null ? t("dashboard_advisor_status_waiting") : t("dashboard_advisor_status_waiting_for", { time: formatDuration(mins) })}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </section>
                <section className="dash-advisor-table">
                    <h4 className="dash-advisor-table-title">{t("dashboard_advisor_inflight_title")}</h4>
                    {s.inflight.length === 0 ? (
                        <p className="dash-advisor-empty">{t("dashboard_advisor_inflight_none")}</p>
                    ) : (
                        <table className="dash-table dash-advisor-dash-table">
                            <thead>
                                <tr>
                                    <th>{t("dashboard_advisor_col_wave")}</th>
                                    <th>{t("dashboard_advisor_col_type")}</th>
                                    <th>{t("dashboard_advisor_col_orders")}</th>
                                    <th>{t("dashboard_advisor_col_picked")}</th>
                                    <th>{t("dashboard_advisor_col_lands")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {s.inflight.map((x) => (
                                    <tr key={x.wave_id} data-testid={`advisor-inflight-row-${x.wave_id}`}>
                                        <td className="dash-td-primary" data-label={t("dashboard_advisor_col_wave")}>
                                            #{x.wave_id}
                                            {x.status === "paused" && <span className="dash-badge dash-badge--paused dash-advisor-inline-badge">{t("dashboard_advisor_status_paused")}</span>}
                                        </td>
                                        <td data-label={t("dashboard_advisor_col_type")}>{typeLabel(x.type)}</td>
                                        <td data-label={t("dashboard_advisor_col_orders")}>{fmtInt(x.orders)}</td>
                                        <td data-label={t("dashboard_advisor_col_picked")}>{fmtInt(x.picked)} / {fmtInt(x.items)}</td>
                                        <td data-label={t("dashboard_advisor_col_lands")}>{x.lands_at ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            </div>
        );
    };

    const today = (w: PickAdvisorWarehouse) => {
        const d = w.today;
        const pct = d.advised > 0 ? Math.round((d.followed / d.advised) * 100) : null;
        return (
            <section className="dash-advisor-today">
                <h4 className="dash-advisor-table-title">{t("dashboard_advisor_today_title")}</h4>
                <p className="dash-advisor-today-summary" data-testid={`advisor-today-${w.warehouse}`}>
                    {t("dashboard_advisor_today_summary", {
                        starts: String(d.starts),
                        advised: String(d.advised),
                        followed: String(d.followed),
                        pct: pct === null ? "—" : `${pct}%`,
                        suggested: String(d.suggested_only),
                    })}
                </p>
                {d.by_rule.length > 0 && (
                    <div className="dash-advisor-chips">
                        {d.by_rule.map((r) => (
                            <span key={r.rule} className="dash-advisor-chip" data-testid={`advisor-rule-chip-${r.rule}`}>
                                <span className="dash-advisor-chip-label">{ruleLabel(r.rule)}</span>
                                <span className="dash-advisor-chip-value">{t("dashboard_advisor_rule_followed", { followed: String(r.followed), advised: String(r.advised) })}</span>
                            </span>
                        ))}
                    </div>
                )}
                {d.recent.length === 0 ? (
                    <p className="dash-advisor-empty">{t("dashboard_advisor_today_none")}</p>
                ) : (
                    // Every start since midnight, newest first, in a panel
                    // that scrolls on its own so a full Pembroke day does
                    // not push the rest of the page away (Alex, 2026-10-16).
                    <div className="dash-advisor-starts-scroll" data-testid={`advisor-starts-${w.warehouse}`}>
                        <table className="dash-table dash-advisor-dash-table">
                            <thead>
                                <tr>
                                    <th>{t("dashboard_advisor_col_time")}</th>
                                    <th>{t("dashboard_advisor_col_picker")}</th>
                                    <th>{t("dashboard_advisor_col_picked_wave")}</th>
                                    <th>{t("dashboard_advisor_col_suggested")}</th>
                                    <th>{t("dashboard_advisor_col_followed")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.recent.map((r) => (
                                    <tr key={r.wave_id} data-testid={`advisor-start-row-${r.wave_id}`}>
                                        <td className="dash-td-primary" data-label={t("dashboard_advisor_col_time")}>{fmtTime(r.ts)}</td>
                                        <td data-label={t("dashboard_advisor_col_picker")}>{r.picker_name ?? "—"}</td>
                                        <td data-label={t("dashboard_advisor_col_picked_wave")}>
                                            {typeLabel(r.pick_type)}{r.orders > 0 ? ` · ${fmtInt(r.orders)}` : ""}{r.tier === "high" ? ` · ${t("dashboard_advisor_high_tier")}` : ""}
                                            <span className="dash-advisor-wave-id"> #{r.wave_id}</span>
                                        </td>
                                        <td data-label={t("dashboard_advisor_col_suggested")}>
                                            {r.rule ? `${typeLabel(r.recommended)} · ${ruleLabel(r.rule)}` : "—"}
                                        </td>
                                        <td data-label={t("dashboard_advisor_col_followed")}>
                                            {followedBadge(r.followed)}
                                            {r.suggested_only === false && r.followed !== null && (
                                                <span className="dash-advisor-stat-sub dash-advisor-inline-sub">{t("dashboard_advisor_toggle_off")}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        );
    };

    return (
        <div className="dash-advisor">
            <div className="dash-planner-controls">
                <p className="admin-settings-section-hint dash-planner-hint dash-advisor-hint">{t("dashboard_advisor_hint")}</p>
                <div className="dash-planner-actions">
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setReloadTick((n) => n + 1)}
                        disabled={loading}
                    >
                        <RefreshCw size={14} />
                        {loading ? t("loading") : t("dashboard_planner_refresh")}
                    </button>
                </div>
            </div>

            {failed && <p className="dash-planner-error">{t("dashboard_advisor_load_error")}</p>}

            {payload && !payload.enabled && (
                <p className="dash-advisor-banner" data-testid="advisor-off">
                    <AlertTriangle size={16} />
                    {t("dashboard_advisor_off_banner")}
                </p>
            )}

            {payload && payload.warehouses.length === 0 && !failed && (
                <p className="dash-advisor-empty" data-testid="advisor-empty">{t("dashboard_advisor_pembroke_only")}</p>
            )}

            {payload?.warehouses.map((w) => (
                <section key={w.warehouse} className="dash-advisor-wh" data-testid={`advisor-wh-${w.warehouse}`} aria-label={w.warehouse}>
                    <div className="dash-advisor-wh-head">
                        <h3 className="dash-advisor-wh-name">{w.warehouse}</h3>
                        <span className="dash-advisor-wh-at">
                            <CheckCircle2 size={12} />
                            {t("dashboard_advisor_state_at", { time: fmtTime(w.state_at || payload.snapshot_at || payload.generated_at) })}
                        </span>
                    </div>
                    {recommendation(w, payload.enabled)}
                    {stageTiles(w)}
                    {waveTables(w)}
                    {today(w)}
                </section>
            ))}
        </div>
    );
};

export default DashboardPickAdvisor;
